import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { Box, Tooltip, Typography } from '@mui/material';

import {
  ActivityActorSummary,
  ActivityActorType,
  ActivityEvent,
  getActivity,
  getActivityActorSummary,
} from '../../api/activityApi/activityApi';
import Loader from '../../components/Loader';
import TablePaginationContainer from '../../components/Table/TablePaginationContainer';
import usePagination from '../../hooks/usePagination';
import useSnackBar from '../../hooks/useSnackBar';
import ActivityEventDrawer from './ActivityEventDrawer';
import {
  MAX_RANGE_DAYS,
  activitySubtitle,
  activityTitle,
  changedFieldNames,
  entityName,
  groupBySession,
  isFailure,
} from './activityHelpers';
import { useActivityStyles } from './activityStyles';

const ActivityActorView = () => {
  const { classes, cx } = useActivityStyles();
  const { setAlert } = useSnackBar();
  const { actorType, actorId } = useParams<{ actorType: ActivityActorType; actorId: string }>();

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const [summary, setSummary] = useState<ActivityActorSummary | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<ActivityEvent | null>(null);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const requestId = useRef(0);

  // The timeline covers the most recent 90 days (the API's maximum range).
  // `from` is measured back from the padded `to` so the span stays within the limit.
  const range = useMemo(() => {
    const to = moment().add(1, 'minute');
    return { from: to.clone().subtract(MAX_RANGE_DAYS, 'days').toDate(), to: to.toDate() };
  }, []);

  const filters = useMemo(
    () => ({ ...range, actorTypes: actorType ? [actorType] : [], actorId: actorId ?? '' }),
    [range, actorType, actorId],
  );

  useEffect(() => {
    if (!actorType || !actorId) return;
    setIsLoading(true);
    getActivityActorSummary(actorType, actorId)
      .then((res) => setSummary(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [actorType, actorId]);

  // The page of the timeline; the total comes with the first page.
  useEffect(() => {
    if (!actorType || !actorId) return;
    const current = ++requestId.current;
    const withCount = pageNumber === 1;
    setIsPageLoading(true);
    getActivity(filters, { page: pageNumber, size: rowsPerPage, count: withCount })
      .then((res) => {
        if (current !== requestId.current) return;
        setEvents(res.data.resultSet);
        if (withCount) setTotalCount?.(res.data.totalCount ?? 0);
      })
      .catch(() => {
        if (current !== requestId.current) return;
        setEvents([]);
        setAlert?.({ message: 'Could not load activity. Please try again.', type: 'error' });
      })
      .finally(() => current === requestId.current && setIsPageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorType, actorId, filters, pageNumber, rowsPerPage]);

  const sessions = useMemo(() => groupBySession(events), [events]);

  const formatDate = (value: string | null) => (value ? moment(value).format('MMM D, YYYY') : '—');

  return (
    <Box className={classes.page}>
      {isLoading ? (
        <Box className={classes.empty}>
          <Loader colorType="warning" />
        </Box>
      ) : notFound || !summary ? (
        <Box className={classes.empty}>No activity found for this person.</Box>
      ) : (
        <>
          <Box className={classes.headerCard}>
            <Typography className={classes.personName}>{summary.name}</Typography>
            <Typography className={classes.subtitle}>{summary.roleLabel}</Typography>
            <Box className={classes.stats}>
              {summary.actorType === 'CUSTOMER' && (
                <Box>
                  <Box className={classes.statLabel}>Matter</Box>
                  <Box className={classes.statValue}>
                    {summary.matters.length
                      ? summary.matters.map((m) => `#${m.referenceId}`).join(', ')
                      : '—'}
                  </Box>
                </Box>
              )}
              <Box>
                <Box className={classes.statLabel}>Sessions</Box>
                <Box className={classes.statValue}>{summary.sessionCount}</Box>
              </Box>
              <Box>
                <Box className={classes.statLabel}>First seen</Box>
                <Box className={classes.statValue}>{formatDate(summary.firstSeenAt)}</Box>
              </Box>
              <Box>
                <Box className={classes.statLabel}>Last seen</Box>
                <Box className={classes.statValue}>{formatDate(summary.lastSeenAt)}</Box>
              </Box>
            </Box>
          </Box>

          {isPageLoading && (
            <Box className={classes.empty}>
              <Loader colorType="warning" />
            </Box>
          )}

          {!isPageLoading && sessions.length === 0 && (
            <Box className={classes.empty}>No activity in the last {MAX_RANGE_DAYS} days.</Box>
          )}

          {!isPageLoading &&
            sessions.map((session) => (
              <Box key={`${session.key}-${session.end}`} className={classes.sessionCard}>
                {session.events.map((event) => (
                  <Box
                    key={event.id}
                    className={cx(classes.timelineRow, classes.clickableRow)}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <Box className={classes.time}>{moment(event.occurredAt).format('h:mm A')}</Box>
                    <Box
                      className={cx(
                        classes.timelineDot,
                        isFailure(event) && classes.timelineDotFailed,
                      )}
                    />
                    <Box>
                      <Box className={classes.strong}>
                        {activityTitle(event)}
                        {isFailure(event) && <span className={classes.failedBadge}>FAILED</span>}
                      </Box>
                      <Tooltip title={changedFieldNames(event)} placement="bottom-start">
                        <Box className={classes.muted}>
                          {[event.subjectLabel, activitySubtitle(event)]
                            .filter(Boolean)
                            .join(' · ')}
                          {event.changedFieldCount > 0 && (
                            <span className={classes.detailsLink}>View changes</span>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                    <span className={classes.entityPill}>{entityName(event.subjectType)}</span>
                  </Box>
                ))}
              </Box>
            ))}

          {!isPageLoading && events.length > 0 && (
            <Box overflow="auto">
              <TablePaginationContainer />
            </Box>
          )}
        </>
      )}

      <ActivityEventDrawer
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        showPersonLink={false}
      />
    </Box>
  );
};

export default ActivityActorView;
